import {type RunHistoryKeyType} from '../../types/run';
import {prettifyMetricName} from '../../util/plotHelpers/prettifyMetricName';

type MetricsPickerKeyTypes = {
  [key: string]: RunHistoryKeyType;
};

const sysRegex = /^system/;
const bottomSortMetrics = new Set([
  '_runtime',
  '_step',
  '_timestamp',
  '_absolute_runtime',
]);

function getKeyTypeText(opt: string, keyType: RunHistoryKeyType | undefined) {
  const prettyMetricName = prettifyMetricName(opt);
  if (keyType === 'number') {
    return prettyMetricName;
  }
  return prettyMetricName + ' (Histogram)';
}

function getOption(opt: string, keyType: RunHistoryKeyType | undefined) {
  return {
    icon: keyType === 'number' ? 'hashtag' : 'list',
    text: getKeyTypeText(opt, keyType),
    value: opt,
    key: opt,
  };
}

export function getYAxisDropdownOptionText(
  option: string,
  keyTypes: MetricsPickerKeyTypes
) {
  return getKeyTypeText(option, keyTypes[option]);
}

export function getYAxisDropdownOption(
  option: string,
  keyTypes: MetricsPickerKeyTypes
) {
  return getOption(option, keyTypes[option]);
}

export function getOrderedYAxisMetricKeys(
  options: string[],
  keyTypes: MetricsPickerKeyTypes
) {
  const regular: string[] = [];
  const histogram: string[] = [];
  const system: string[] = [];
  const reserved: string[] = [];

  for (const option of options) {
    const keyType = keyTypes[option];
    if (bottomSortMetrics.has(option)) {
      reserved.push(option);
    } else if (sysRegex.test(option)) {
      system.push(option);
    } else if (keyType !== 'number') {
      histogram.push(option);
    } else {
      regular.push(option);
    }
  }

  return [...regular, ...histogram, ...system, ...reserved];
}

export function getYAxisDropdownOptionsForKeys(
  options: string[],
  keyTypes: MetricsPickerKeyTypes
) {
  return options.map(option => getYAxisDropdownOption(option, keyTypes));
}

/**
 * Searchable display texts, one per key, in key order. Built once per key set
 * (lazily, on the first search) and scanned by `pickerSearchIndex`. This is
 * what hoists `prettifyMetricName` — a cascade of regexes per system-metric
 * key — out of the per-search loop.
 */
export function buildYAxisSearchTexts(
  options: readonly string[],
  keyTypes: MetricsPickerKeyTypes
): string[] {
  return options.map(option => getYAxisDropdownOptionText(option, keyTypes));
}

export function getVisibleYAxisMetricKeys(
  orderedOptions: string[],
  selectedValues: string[] | undefined,
  resultLimit: number
) {
  const visibleOptions = orderedOptions.slice(0, resultLimit + 1);
  if (selectedValues == null) {
    return visibleOptions;
  }

  const visibleSet = new Set(visibleOptions);
  const selectedMissingFromVisible = selectedValues.filter(
    value => !visibleSet.has(value)
  );
  return [...selectedMissingFromVisible, ...visibleOptions];
}

export function getYAxisDropdownOptions(
  options: string[],
  keyTypes: MetricsPickerKeyTypes
) {
  // Keep this linear. Sorting thousands of metric strings on every mount shows
  // up clearly in EditDataTab traces.
  return getYAxisDropdownOptionsForKeys(
    getOrderedYAxisMetricKeys(options, keyTypes),
    keyTypes
  );
}
